import type { ListedPiece } from "./catalog-types";

/**
 * The catalog search over pieces already in hand — the host's own list and the guest's page
 * (filters spec §3, prototype `casaQ`). One term, two entities: it matches the piece's **name or
 * its SKU**, each with its own normalisation. The served catalogue searches on the server instead.
 *
 * It used to match name + collection. The handoff names SKU, not collection, so searching by a
 * collection's name no longer finds its pieces here (the collection has its own facet).
 */

/** "Árvores do Brasil — Taça" → "arvores do brasil taca". */
export function normalizeSearchText(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * "000.001-460" → "000001460": punctuation (and spacing) dropped on **both** sides, so a code
 * typed with or without its separators finds the same piece (handoff §3, "Normalização de SKU").
 */
export function normalizeSku(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[^a-z0-9]+/g, "");
}

/** An empty term matches every piece. */
export function matchesCatalogSearch(piece: Pick<ListedPiece, "name" | "sku">, term: string): boolean {
  const nameQuery = normalizeSearchText(term);
  if (nameQuery === "") return true;

  if (normalizeSearchText(piece.name).includes(nameQuery)) return true;

  // A piece without a code (the list's own items carry none yet) can only match by name.
  const sku = normalizeSku(piece.sku ?? "");
  if (sku === "") return false;

  // Matched by fragment: part of a code finds it, as the handoff asks.
  return sku.includes(normalizeSku(term));
}
